import { api } from "@/lib/api"
import { useFormStore } from "@/stores/use-form-store"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { usePayment } from "../../use-payment"
import { toast } from "sonner"

// Submit Offline Payment Types
type ADJUSTMENT = {
	amount: number
	currency: string
	type: string
	description: string
}
type FEE_ITEM = {
	fee_component_id: string
	component_dimension_id: string
	amount: number
	currency: string
	period_type: string
	period_number: number
}
type ORDER = {
	application_id: string
	batch_id: string
	course_id: string
	fee_grid_revision_id: string
	category: string
	plan: string
	// period_number: number
	currency: string
	mode: "SELF" | "LOAN"
	method: string
	created_at: string
	items: FEE_ITEM[]
	adjustments: ADJUSTMENT[]
	additional_info: Record<string, unknown>
	semester: number
}

interface OFFLINE_PAYMENT_SUBMIT_REQUEST {
	reference_number: string
	account_holder_name: string
	amount: number
	currency: string
	mode: string
	bank_name: string
	proof_url: File
	paid_at: string
	remarks: string
	order: ORDER
	additional_info: Record<string, unknown>
}

interface OFFLINE_PAYMENT_LOAN_SUBMIT_REQUEST {
	lender_loan_id: string
	lender: string
	loan_amount: number
	currency: string
	proof_url: File
	status: "PENDING_VERIFICATION" | "APPROVED" | "REJECTED"
	applicant_name: string
	applicant_mobile_number: string
	applicant_email: string
	applicant_relation: string
	remarks: string
	order: ORDER
	additional_info: Record<string, unknown>
}

interface OFFLINE_PAYMENT_RESPONSE {
	message: string
	success: string
}

interface OFFLINE_PAYMENT_LOAN_RESPONSE {
	message: string
	success: string
}

interface GET_OFFLINE_PAYMENT_DETAILS {
	reference_number: string
	account_holder_name: string
	application_id: string
	batch_id: string
	course_id: string
	amount: string
	currency: string
	adjustment_amount: string
	mode: string
	bank_name: string
	status: string
	comments: string
	paid_at: string
	remarks: string
	proof_url: string
	program_name: string
	specialization_name: string
	enrollment_no: string
	student_name: string
	partner_name: string
	additional_info: Record<string, unknown>
	payment_id: string
	created_at: string
	updated_at: string
}

interface GET_OFFLINE_LOAN_PAYMENT_DETAILS {
	application_id: string
	course_id: string
	lender_loan_id: string
	lender: string
	loan_amount: number
	currency: string
	proof_url: string
	status: string
	plan: string
	applicant_name: string
	applicant_mobile_number: string
	applicant_email: string
	applicant_relation: string
	additional_info: Record<string, unknown>
	remarks: string
	comments: string
	updated_by: string
	student_details: {
		student_name: string
		enrollment_number: string
		program_name: string
		specialization_name: string
		batch_name: string
		partner_name: string
		discount_amount: number
	}
	loan_details: {
		loan_id: string
		loan_amount: number
		loan_tenure: number
		loan_model: "SUBVENTION" | "ROI"
		interest_rate: number
		disbursed_amount: number
		disbursed_date: string
	}
}
export const useOfflinePayment = () => {
	const { config, applicationId } = useFormStore()
	const { course_id } = usePayment()
	const queryClient = useQueryClient()
	// Submit offline payment API
	const useSubmitOfflinePayment = () => {
		return useMutation<
			OFFLINE_PAYMENT_RESPONSE,
			AxiosError<{ message: string }>,
			OFFLINE_PAYMENT_SUBMIT_REQUEST
		>({
			mutationFn: async (data: OFFLINE_PAYMENT_SUBMIT_REQUEST) => {
				const formData = new FormData()
				formData.append("reference_number", data.reference_number)
				formData.append("account_holder_name", data.account_holder_name)
				formData.append("amount", data.amount.toString())
				formData.append("currency", data.currency)
				formData.append("mode", data.mode)
				formData.append("bank_name", data.bank_name)
				formData.append("proof_url", data.proof_url)
				formData.append("remarks", data.remarks)
				formData.append("order", JSON.stringify(data.order))
				formData.append("paid_at", new Date(data.paid_at).toISOString())
				formData.append(
					"additional_info",
					JSON.stringify(data.additional_info),
				)
				formData.append("status", "PENDING_VERIFICATION")
				const response = await api.post(
					`/universities/${config?.university_id}/batches/${config?.batch_id}/offline-payments/self`,
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				)
				return response.data
			},
			onSuccess: (data: OFFLINE_PAYMENT_RESPONSE) => {
				// invalidate the payment status query
				toast.success(data.message)
				queryClient.invalidateQueries({
					queryKey: [
						"check-payment-status",
						applicationId,
						config?.batch_id,
						course_id,
						window.__DF_MODE,
					],
				})
			},
			onError: (error: AxiosError<{ message: string }>) => {
				toast.error(error.response?.data.message)
			},
		})
	}

	const useSubmitOfflineLoan = () => {
		return useMutation<
			OFFLINE_PAYMENT_LOAN_RESPONSE,
			AxiosError<{ message: string }>,
			OFFLINE_PAYMENT_LOAN_SUBMIT_REQUEST
		>({
			mutationFn: async (data: OFFLINE_PAYMENT_LOAN_SUBMIT_REQUEST) => {
				const formData = new FormData()
				formData.append("lender_loan_id", data.lender_loan_id)
				formData.append("lender", data.lender)
				formData.append("loan_amount", data.loan_amount.toString())
				formData.append("currency", data.currency)
				formData.append("proof_url", data.proof_url)
				formData.append("order", JSON.stringify(data.order))
				formData.append(
					"additional_info",
					JSON.stringify(data.additional_info),
				)
				formData.append("applicant_country_code", "+91")
				formData.append("status", data.status)
				formData.append("applicant_name", data.applicant_name)
				formData.append(
					"applicant_mobile_number",
					data.applicant_mobile_number,
				)
				formData.append("applicant_email", data.applicant_email)
				formData.append("applicant_relation", data.applicant_relation)
				formData.append("remarks", data.remarks)
				const response = await api.post(
					`/universities/${config?.university_id}/batches/${config?.batch_id}/offline-payments/loans`,
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				)
				return response.data
			},
			onSuccess: (data: OFFLINE_PAYMENT_LOAN_RESPONSE) => {
				toast.success(data.message)
				queryClient.invalidateQueries({
					queryKey: [
						"check-payment-status",
						applicationId,
						config?.batch_id,
						course_id,
						window.__DF_MODE,
					],
				})
			},
			onError: (error: AxiosError<{ message: string }>) => {
				toast.error(error.response?.data.message)
			},
		})
	}
	const useGetOfflinePaymentDetails = ({
		payment_id,
	}: {
		payment_id: string
	}) => {
		return useQuery<GET_OFFLINE_PAYMENT_DETAILS, AxiosError>({
			queryFn: async () => {
				const { data } = await api.get(
					`/universities/${config?.university_id}/batches/${config?.batch_id}/offline-payments/self/${payment_id}`,
				)
				return data
			},
			queryKey: [
				"offline-payment-details",
				config?.university_id,
				config?.batch_id,
				payment_id,
			],
			enabled:
				!!config?.university_id && !!config?.batch_id && !!payment_id,
		})
	}

	const useGetOfflineLoanPaymentDetails = ({
		loan_id,
	}: {
		loan_id: string
	}) => {
		return useQuery<GET_OFFLINE_LOAN_PAYMENT_DETAILS, AxiosError>({
			queryFn: async () => {
				const { data } = await api.get(
					`/universities/${config?.university_id}/batches/${config?.batch_id}/offline-payments/loans/${loan_id}`,
				)
				return data
			},
			queryKey: [
				"offline-loan-payment-details",
				config?.university_id,
				config?.batch_id,
				loan_id,
			],
			enabled: !!config?.university_id && !!config?.batch_id && !!loan_id,
		})
	}

	const useGetFile = () => {
		return useMutation<
			File,
			AxiosError,
			{ file_url: string; file_name?: string }
		>({
			mutationFn: async ({ file_url, file_name }) => {
				const response = await api.get("media", {
					params: {
						path: file_url,
					},
					responseType: "arraybuffer",
				})
				const mimeType =
					response.headers["content-type"] || "application/pdf"

				let finalFilename = file_name

				if (!finalFilename) {
					const contentDisposition =
						response.headers["content-disposition"]
					if (contentDisposition) {
						const filenameMatch = contentDisposition.match(
							/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
						)
						if (filenameMatch && filenameMatch[1]) {
							finalFilename = filenameMatch[1].replace(
								/['"]/g,
								"",
							)
						}
					}
				}

				if (!finalFilename) {
					const urlFilename = file_url.split("/").pop()
					if (urlFilename && urlFilename.includes(".")) {
						finalFilename = urlFilename
					}
				}

				if (!finalFilename) {
					finalFilename = "document.pdf"
				}

				const blob = new Blob([response.data], { type: mimeType })
				return new File([blob], finalFilename, { type: mimeType })
			},
			mutationKey: ["get-file"],
		})
	}
	const useValidateTransactionReference = ({
		reference_number,
	}: {
		reference_number: string
	}) => {
		return useQuery<boolean, AxiosError>({
			queryFn: async () => {
				const { data } = await api.get(
					`/offline-payments/self/validate`,
					{
						params: {
							reference_number,
						},
					},
				)
				return data?.exists
			},
			queryKey: ["validate-transaction-reference", reference_number],
			enabled: !!reference_number,
			retry: false,
		})
	}

	return {
		useSubmitOfflineLoan,
		useSubmitOfflinePayment,
		useGetOfflinePaymentDetails,
		useGetOfflineLoanPaymentDetails,
		useGetFile,
		useValidateTransactionReference,
	}
}
